
import { createContext, useContext, useState, type ReactNode } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { estimatesApi } from "@/lib/api/estimates"
import { queryKeys } from "@/lib/api/query-keys"
import type { VehicleSide } from "@/lib/api/types"
import type { TeamsData } from "@/lib/api/use-teams"

interface EstimatePhoto {
  id: string
  url: string
  side: VehicleSide | string
  quality: number
  description: string
}

interface EstimateData {
  customer_name: string
  customer_email: string
  customer_phone: string
  vehicle_vin: string
  vehicle_year: string
  vehicle_make: string
  vehicle_model: string
  vehicle_trim: string
  notes: string
  photos: EstimatePhoto[]
  teamId: number | string | null
}

interface SaveEstimateResult {
  estimateId: number
  jobId: string | null
}

interface EstimateContextType {
  currentStep: number
  totalSteps: number
  data: EstimateData
  setCurrentStep: (step: number) => void
  updateData: (updates: Partial<EstimateData>) => void
  updateCustomerInfo: (info: Partial<EstimateData>) => void
  updateVehicleInfo: (info: Partial<EstimateData>) => void
  addPhoto: (photo: EstimatePhoto) => void
  removePhoto: (id: string) => void
  updateNotes: (notes: string) => void
  saveEstimate: (status?: string, overridePhotos?: EstimatePhoto[]) => Promise<SaveEstimateResult>
  nextStep: () => void
  previousStep: () => void
}

const EstimateContext = createContext<EstimateContextType | undefined>(undefined)

const TOTAL_STEPS = 3

const DEFAULT_DATA: EstimateData = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  vehicle_vin: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_trim: "",
  notes: "",
  photos: [],
  teamId: null,
}

interface EstimateProviderProps {
  children: ReactNode
  initialData?: Partial<EstimateData>
}

export function EstimateProvider({ children, initialData }: EstimateProviderProps) {
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(0)
  const [data, setData] = useState<EstimateData>({
    ...DEFAULT_DATA,
    ...initialData,
  })

  const updateData = (updates: Partial<EstimateData>) => {
    setData((prev) => ({ ...prev, ...updates }))
  }

  const updateCustomerInfo = (info: Partial<EstimateData>) => {
    setData((prev) => ({ ...prev, ...info }))
  }

  const updateVehicleInfo = (info: Partial<EstimateData>) => {
    setData((prev) => ({ ...prev, ...info }))
  }

  const addPhoto = (photo: EstimatePhoto) => {
    setData((prev) => ({ ...prev, photos: [...prev.photos, photo] }))
  }

  const removePhoto = (id: string) => {
    setData((prev) => ({ ...prev, photos: prev.photos.filter((p) => p.id !== id) }))
  }

  const updateNotes = (notes: string) => {
    setData((prev) => ({ ...prev, notes }))
  }

  /**
   * Create the estimate on the real backend.
   * 1. POST /estimates/team/:teamId/full (customer + vehicle)
   * 2. POST /estimates/:id/images (if photos exist)
   * 3. POST /estimates/:id/damage with quickEstimateMode (triggers AI agent chain)
   * Returns the estimateId and jobId for polling.
   */
  const saveEstimate = async (_status?: string, overridePhotos?: EstimatePhoto[]): Promise<SaveEstimateResult> => {
    let teamId = data.teamId
    if (!teamId) {
      // Fall back to active team from query cache
      const teamsData = queryClient.getQueryData<TeamsData>(queryKeys.teams.list())
      const fallback = teamsData?.dealerships[0] ?? teamsData?.bodyShops[0]
      teamId = fallback ? Number(fallback.id) : null
    }
    if (!teamId) {
      throw new Error("Team ID is required to create an estimate")
    }

    // 1. Create estimate
    const estimate = await estimatesApi.startFull(teamId, {
      customer: {
        name: data.customer_name,
        email: data.customer_email || undefined,
        phone: data.customer_phone || undefined,
      },
      vehicle: {
        vin: data.vehicle_vin,
        make: data.vehicle_make || undefined,
        model: data.vehicle_model || undefined,
        year: data.vehicle_year || undefined,
        trim: data.vehicle_trim || undefined,
      },
    })

    // 2. Add images if any (use overridePhotos if provided, else context photos)
    const photosToSend = overridePhotos ?? data.photos
    if (photosToSend.length > 0) {
      const sideMap: Record<string, string> = {
        front: 'FRONT_CENTER', rear: 'REAR_CENTER',
        left: 'LEFT_SIDE', right: 'RIGHT_SIDE',
      }
      try {
        await estimatesApi.addImages(estimate.id, {
          images: photosToSend.map((p) => ({
            imageUrl: p.url,
            side: (sideMap[p.side] || p.side || 'FRONT_CENTER') as any,
            qualityScore: p.quality || 80,
            qualified: (p.quality || 80) >= 50,
            description: p.description || `${p.side} view`,
          })),
        })
      } catch {
        console.warn('Failed to add images')
      }
    }

    // 3. Trigger damage estimation (quick mode chains all agents)
    let jobId: string | null = null
    try {
      const agentResult = await estimatesApi.runDamageEstimation(estimate.id, { quickEstimateMode: true })
      jobId = agentResult.jobId ?? null
    } catch {
      console.warn('Failed to trigger damage estimation')
    }

    return { estimateId: estimate.id, jobId }
  }

  const nextStep = () => {
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  return (
    <EstimateContext.Provider
      value={{
        currentStep,
        totalSteps: TOTAL_STEPS,
        data,
        setCurrentStep,
        updateData,
        updateCustomerInfo,
        updateVehicleInfo,
        addPhoto,
        removePhoto,
        updateNotes,
        saveEstimate,
        nextStep,
        previousStep,
      }}
    >
      {children}
    </EstimateContext.Provider>
  )
}

export function useEstimate() {
  const context = useContext(EstimateContext)
  if (context === undefined) {
    throw new Error("useEstimate must be used within an EstimateProvider")
  }
  return context
}
